<template>
  <v-data-table :value="value" v-bind="$attrs" @input="updateValue" hide-default-footer>
    <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope" />
    </template>

    <template v-slot:footer="{ props, on }">
      <div v-if="!hideFooter" class="v-data-footer">
        <div class="v-data-footer__select">
          Items per page:
          <v-select
            label=""
            :items="itemsPerPageOptions"
            :value="props.options.itemsPerPage"
            :disabled="false"
            :hide-details="true"
            min-width="75px"
            :auto="true"
            @input="val => onItemsPerPageChange(val, props.options, on)">
          </v-select>
        </div>
        <div class="v-data-footer__pagination mr-1">
          {{ props.pagination.itemsLength > 0 ? props.pagination.pageStart + 1 : 0 }} -
          {{ props.pagination.pageStop }} of {{ props.pagination.itemsLength }}
        </div>

        <div
          @click="() => onPaginationClick(props.pagination.page - 1, props.options, props.pagination, on)"
          class="pagination__item pagination__item--arrow"
          :class="props.pagination.page === 1 ? 'pagination__item--disabled' : ''">
          <v-icon color="icon">mdi-chevron-left</v-icon>
        </div>
        <template v-for="page in getPaginationItems(props.pagination)">
          <div
            v-if="page.value"
            :key="page.key"
            @click="() => onPaginationClick(page.value, props.options, props.pagination, on)"
            class="pagination__item"
            :class="[page.value === props.pagination.page ? 'pagination__item--active' : '']">
            {{ page.label }}
          </div>
          <div v-else :key="page.key" class="pagination__item pagination__item--empty">
            {{ page.label }}
          </div>
        </template>
        <div
          @click="() => onPaginationClick(props.pagination.page + 1, props.options, props.pagination, on)"
          class="pagination__item pagination__item--arrow"
          :class="props.pagination.page >= props.pagination.pageCount ? 'pagination__item--disabled' : ''">
          <v-icon color="icon">mdi-chevron-right</v-icon>
        </div>
      </div>
    </template>
  </v-data-table>
</template>

<script>
  import { v4 as uuidv4 } from "uuid";
  import { LIST_ITEMS_PER_PAGE } from "@/constant";
  export default {
    name: "VaClientSideList",
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      hideFooter: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        itemsPerPageOptions: LIST_ITEMS_PER_PAGE,
      };
    },
    watch: {
      $attrs: {
        deep: true,
        immediate: true,
        handler(attrs) {
          const itemsPerPage = attrs["items-per-page"] || attrs?.options?.itemsPerPage;
          if (itemsPerPage && !this.itemsPerPageOptions.includes(itemsPerPage)) {
            this.itemsPerPageOptions.push(itemsPerPage);
            this.itemsPerPageOptions.sort((a, b) => a - b);
          }
        },
      },
    },
    methods: {
      onItemsPerPageChange(val, currentOptions, slotOn) {
        const newOptions = { ...currentOptions, itemsPerPage: val };
        // Emit to parent
        this.$emit("update:options", newOptions);
        // Call Vuetify's internal slot handler
        slotOn?.["update:options"]?.(newOptions);
      },
      onPaginationClick(targetPage, currentOptions, currentPagination, slotOn) {
        if (currentPagination.page === targetPage) return;
        const newOptions = { ...currentOptions, page: targetPage };
        // Emit to parent
        this.$emit("update:options", newOptions);
        // Call Vuetify's internal slot handler
        slotOn?.["update:options"]?.(newOptions);
      },
      updateValue(value) {
        this.$emit("input", value);
      },
      getPaginationItems(paginationProps) {
        const { page: currentPage, pageCount } = paginationProps;

        let pages = [];

        if (pageCount <= 6) {
          return this.getPagesRange(pageCount, 1);
        } else {
          pages.push(this.getPageItem(1));

          if (currentPage > 3) {
            pages.push(this.getEmptyPageItem());
          }

          if ([1, 2, 3].includes(currentPage)) {
            pages = pages.concat(this.getPagesRange(4, 2));
          } else if ([pageCount, pageCount - 1, pageCount - 2].includes(currentPage)) {
            pages = pages.concat(this.getPagesRange(5, pageCount - 4));
          } else {
            pages = pages.concat(this.getPagesRange(3, currentPage - 1));
          }

          if (currentPage < pageCount - 2) {
            pages.push(this.getEmptyPageItem());
          }

          if (currentPage !== pageCount && currentPage !== pageCount - 1 && currentPage !== pageCount - 2) {
            pages.push(this.getPageItem(pageCount));
          }

          return pages;
        }
      },
      getPageItem(page) {
        return {
          value: page,
          label: page,
          key: uuidv4(),
        };
      },
      getEmptyPageItem() {
        return {
          value: "",
          label: "...",
          key: uuidv4(),
        };
      },
      getPagesRange(size, startAt = 0) {
        return [...Array(size).keys()].map(i => {
          return this.getPageItem(i + startAt);
        });
      },
    },
  };
</script>

<style scoped lang="scss">
  @import "../../@core/preset/preset/typography/index.scss";
  @import "../../@core/preset/preset/functions.scss";
  .v-data-footer {
    display: flex;
    align-items: center;
    gap: 6px;
    &__select {
      margin-left: 0px !important;
    }
    .pagination {
      &__item {
        padding: 4px 10px;
        border-radius: 4px;
        &:not(.pagination__item--active, .pagination__item--empty, .pagination__item--disabled) {
          &:hover {
            background-color: get-color("primary-10-hover");
          }
        }
        &:not(.pagination__item--disabled, .pagination__item--empty) {
          cursor: pointer;
        }
        &--active {
          background-color: get-color("primary-20");
          color: get-color("primary");
          @include text-caption();
        }
        &--arrow {
          padding: 0;
        }
        &--empty {
          color: get-color("subtitle");
          @include text-caption();
        }
        &--disabled {
          .v-icon {
            color: get-color("element-bg-300") !important;
          }
        }
      }
    }
  }
</style>
